import type { ErrorInfo } from "react"

import { createLogFlusher, debounce, type FlushOptions } from "./flushLogs"
import {
  installFlushOnHidden,
  installGlobalErrorHandlers,
} from "./globalHandlers"
import { toErrorData } from "./toErrorData"
import type { ErrorData, ErrorLogger, ErrorLoggerConfig } from "./types"

export const DEBOUNCE_WAIT_MS = 2000
export const DEBOUNCE_MAX_WAIT_MS = 10_000
export const MAX_PENDING_ERRORS = 50

export const createErrorLogger = (config: ErrorLoggerConfig): ErrorLogger => {
  const flusher = createLogFlusher(config)
  const debouncedFlush = debounce(
    () => {
      void flusher.flush()
    },
    {
      wait: DEBOUNCE_WAIT_MS,
      maxWait: DEBOUNCE_MAX_WAIT_MS,
    },
  )

  const flushNow = (options?: FlushOptions) => {
    debouncedFlush.cancel()
    void flusher.flush(options)
  }

  const enqueue = (errorData: ErrorData) => {
    const { shouldFlush } = flusher.enqueue(errorData)
    if (shouldFlush) {
      flushNow()
      return
    }
    debouncedFlush()
  }

  const logError = (error: unknown, errorInfo?: ErrorInfo | null) => {
    enqueue(toErrorData(error, errorInfo))
  }

  const teardowns = [
    installGlobalErrorHandlers(logError),
    installFlushOnHidden(() => flushNow({ keepalive: true })),
  ]

  const dispose = () => {
    for (const teardown of teardowns.toReversed()) teardown()
    teardowns.length = 0
    flushNow({ keepalive: true })
  }

  return { logError, enqueue, flush: () => flusher.flush(), dispose }
}

let activeLogger: ErrorLogger | undefined = undefined
let pendingErrors: ErrorData[] = []

export const initLogger = (config: ErrorLoggerConfig): ErrorLogger => {
  activeLogger?.dispose()
  activeLogger = createErrorLogger(config)

  for (const errorData of pendingErrors) activeLogger.enqueue(errorData)
  pendingErrors = []

  return activeLogger
}

export const disposeLogger = (): void => {
  activeLogger?.dispose()
  activeLogger = undefined
  pendingErrors = []
}

export const getErrorLogger = (): ErrorLogger | undefined => activeLogger

export const logError = (
  error: unknown,
  errorInfo?: ErrorInfo | null,
): void => {
  if (activeLogger) {
    activeLogger.logError(error, errorInfo)
    return
  }

  pendingErrors.push(toErrorData(error, errorInfo))
  if (pendingErrors.length > MAX_PENDING_ERRORS) {
    pendingErrors = pendingErrors.slice(-MAX_PENDING_ERRORS)
  }
}
